import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
} from "@mui/material";
import React from "react";
import { StaticDatePicker } from "@mui/x-date-pickers";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import StepperGuide from "src/components/StepperGuide";
import TimezonePicker from "src/views/general/timezone-picker";
import TreffasTextField from "src/components/TreffasTextField";
import Timezone from "src/utils/Timezone";
import { ArrowDropDown } from "@mui/icons-material";
import TreffasStyle from "src/utils/TreffasStyle";
import { useFacebookPixelPublic } from "src/views/hooks/useFacebookPixelPublic";
import { useGoogleAnalyticsPublic } from "src/views/hooks/useGoogleAnalyticsPublic";
import { useDateAndTimePicker } from "src/views/hooks/useDateAndTimePicker";

const DateAndTime = ({
  selectedTimezone,
  setSessionSelectedTime,
  selectedOffer,
  setSessionList,
  selectedDate,
  setSelectedDate,
  setError,
  clearErrors,
  calendarMaxDate,
  userDateFormat,
  setAllUpcommingAppt,
  preparingSessionList,
  setPreparingSesisonList,
  stepperGuideLength,
  setActiveStep,
  coachDetails,
  setOverFlowTimeToRender,
  setUnderFlowTimeToRender,
  setSelectedTimezone,
  isUseTimeRecord,
  setIsUseTimeRecord,
  timeRecords,
  setTimeRecords,
  customStyle,
}) => {
  const { t } = useTranslation();
  const { userId, offerId, organizationId } = useParams();
  const [rerenderPixel] = React.useState(1);
  const {
    shouldDisabledDate,
    handleSelectedDate,
    handleTimezoneChange,
    handleMonthChange,
    openTimezoneDialog,
    closeTimezoneDialog,
    timezoneDialog,
    datePickerMinDate,
    datePickerMaxDate,
    pickerValue,
  } = useDateAndTimePicker({
    selectedOffer,
    coachDetails,
    selectedTimezone,
    selectedDate,
    setSelectedDate,
    setSelectedTimezone,
    setSessionList,
    setSessionSelectedTime,
    setOverFlowTimeToRender,
    setUnderFlowTimeToRender,
    setAllUpcommingAppt,
    isUseTimeRecord,
    setIsUseTimeRecord,
    timeRecords,
    setTimeRecords,
    preparingSessionList,
    setPreparingSesisonList,
    clearErrors,
    calendarMaxDate,
    offerId,
    userId,
    organizationId,
    userDateFormat,
    setError,
    mode: "mobile",
    setActiveStep,
  });

  const user_settings = coachDetails?.settings
    ? JSON.parse(coachDetails?.settings)
    : null;
  const __analytics = user_settings?.analytics ?? null;
  let enable = __analytics?.has_meta_pixel ?? false;
  let pixelId = __analytics?.meta_pixel ?? null;
  let googleAnalyticsEnable = __analytics?.has_google_analytics ?? false;
  let gaId = __analytics?.google_analytics ?? null;

  const trackEventInFacebook = useFacebookPixelPublic({
    track: "ViewContent",
    content_name: `${selectedOffer?.name ?? ""} Create booking url`,
    content_description: `${
      selectedOffer?.description ?? "Package create booking url."
    }`,
    enable: enable,
    pixelId: pixelId,
  });

  const trackEventInGoogleAnalytics = useGoogleAnalyticsPublic({
    enable: googleAnalyticsEnable,
    gaId: gaId,
  });

  React.useEffect(() => {
    trackEventInFacebook();
    trackEventInGoogleAnalytics("view_content", {
      content_name: `${selectedOffer?.name ?? ""} Create booking url`,
      content_description: `${
        selectedOffer?.description ?? "Package create booking url."
      }`,
    });
  }, [rerenderPixel]);

  return (
    <>
      <Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: "32px",
            pt: "42px",
            flexDirection: "column",
          }}
        >
          <Box>
            <StepperGuide
              length={stepperGuideLength}
              activeLength={[1]}
              customStyle={customStyle}
            />
          </Box>

          <Box>
            <Typography
              sx={{
                fontFamily:
                  customStyle?.headingFont ?? "var(--font-grenettepromedium)",
                fontSize: "18px",
                lineHeight: "31px",
                alignItems: "center",
                color: customStyle?.textColor ?? "#060A2D",
              }}
            >
              {t("individual.booking.schedule.choose_schedule")}
            </Typography>

            <Typography
              sx={{
                fontWeight: 400,
                fontSize: "14px",
                lineHeight: "18px",
                color: customStyle?.secondaryTextColor ?? "#878787",
                fontFamily: `var(--font-${customStyle?.bodyFont})`,
                mt: "4px",
              }}
            >
              {t("individual.booking.schedule.choose_schedule.description", {
                package: selectedOffer?.name,
              })}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box my={"20px"}>
        <Box mb={"10px"}>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "10px",
              lineHeight: "11px",
              letterSpacing: "0.07em",
              color: customStyle?.secondaryTextColor ?? "#878787",
              fontFamily: `var(--font-${customStyle?.bodyFont})`,
              textTransform: "uppercase",
            }}
          >
            {t("booking.label.select_timezone")}
          </Typography>
        </Box>

        <Box sx={{ px: "5px" }}>
          <Box>
            <TreffasTextField
              value={Timezone.getLabel(selectedTimezone) ?? ""}
              sx={TreffasStyle.formInputLarge("autocomplete")}
              placeholder={t("booking.placeholder.select_timezone")}
              fullWidth
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="start">
                    <IconButton
                      size={"small"}
                      onClick={() =>
                        openTimezoneDialog({ resetSelectedDate: true })
                      }
                    >
                      <ArrowDropDown />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Box>
              <TimezonePicker
                dialog={timezoneDialog}
                close={closeTimezoneDialog}
                selectedTimezone={selectedTimezone}
                onChange={handleTimezoneChange}
              />
            </Box>
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          mt: "31px",
        }}
      >
        <Box className={"new-static-calendar"}>
          <Box>
            <StaticDatePicker
              // disablePast
              minDate={datePickerMinDate}
              displayStaticWrapperAs="desktop"
              value={pickerValue}
              shouldDisableDate={shouldDisabledDate}
              onChange={handleSelectedDate}
              onMonthChange={handleMonthChange}
              renderInput={(params) => <TextField {...params} />}
              views={["day"]}
              inputFormat={userDateFormat}
              maxDate={datePickerMaxDate}
            />
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default DateAndTime;
